'use client'

import type { getOrganizationImportStatus } from '@/app/(authenticated)/org/[id]/data-import/actions'
import { useImportStats } from '@/app/(authenticated)/org/[id]/data-import/useImportStats'
import { cn } from '@/lib'
import { cva } from 'cva'
import { LoaderCircle } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import { I18NText } from '../I18NText'
import { paragraphVariants } from '../Typography/Paragraph'

export const navigationVariants = cva({
	base: cn(
		paragraphVariants({ color: 'caption' }),
		'rounded-sm align-middle text-center px-2 py-0.5 border border-transparent hover:bg-purple-25 hover:text-purple-700 cursor-pointer',
	),
	variants: {
		state: {
			default: '',
			active: cn(
				paragraphVariants({ weight: 'bold' }),
				'text-purple-700 bg-purple-100',
			),
		},
	},
	defaultVariants: {
		state: 'default',
	},
})

interface Props {
	organizationId: string
	activityBased: boolean
	showDataImport: boolean
	activeImport: Awaited<ReturnType<typeof getOrganizationImportStatus>>
}

export const Navigation: React.FC<Props> = ({
	organizationId,
	activityBased,
	showDataImport,
	activeImport,
}) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()

	const isOverview = pathname === `/org/${organizationId}`
	const isAccounts = pathname.startsWith(`/org/${organizationId}/accounts`)
	const isEmissions = pathname === `/org/${organizationId}/emissions`
	const isTransactions = pathname.startsWith(
		`/org/${organizationId}/transactions`,
	)
	const isReports = pathname === `/org/${organizationId}/reports`
	const isImport = pathname === `/org/${organizationId}/data-import`

	const { data } = useImportStats(organizationId, activeImport)
	const hasActiveImport = data?.imports && data?.imports.length > 0

	const keepYearParams = isOverview || isEmissions || isTransactions

	const yearSearch = useMemo(() => {
		if (searchParams.get('year')) {
			const yearSearch = new URLSearchParams({
				year: searchParams.get('year') || '',
			})
			return yearSearch
		}
		return undefined
	}, [searchParams])

	return (
		<div className="flex justify-center gap-2">
			<Link
				href={{
					pathname: `/org/${organizationId}`,
					search: keepYearParams ? yearSearch?.toString() : undefined,
				}}
				className={navigationVariants({
					state: isOverview ? 'active' : 'default',
				})}
			>
				<I18NText text="navbar.navigation.overview" />
			</Link>
			{activityBased ? (
				<>
					<Link
						href={{
							pathname: `/org/${organizationId}/emissions`,
							search: keepYearParams ? yearSearch?.toString() : undefined,
						}}
						className={navigationVariants({
							state: isEmissions ? 'active' : 'default',
						})}
					>
						<I18NText text="navbar.navigation.emissions" />
					</Link>
					<Link
						href={{
							pathname: `/org/${organizationId}/transactions`,
							search: keepYearParams ? yearSearch?.toString() : undefined,
						}}
						className={navigationVariants({
							state: isTransactions ? 'active' : 'default',
						})}
					>
						<I18NText text="navbar.navigation.transactions" />
					</Link>
				</>
			) : (
				<Link
					href={{
						pathname: `/org/${organizationId}/accounts`,
						search: keepYearParams ? yearSearch?.toString() : undefined,
					}}
					className={navigationVariants({
						state: isAccounts ? 'active' : 'default',
					})}
				>
					<I18NText text="navbar.navigation.accounts" />
				</Link>
			)}
			<Link
				href={`/org/${organizationId}/reports`}
				className={navigationVariants({
					state: isReports ? 'active' : 'default',
				})}
			>
				<I18NText text="navbar.navigation.reports" />
			</Link>
			{showDataImport && (
				<Link
					href={`/org/${organizationId}/data-import`}
					className={cn(
						navigationVariants({
							state: isImport ? 'active' : 'default',
						}),
						'flex items-center gap-1',
					)}
				>
					<I18NText text="navbar.navigation.dataImport" />
					{hasActiveImport && <LoaderCircle className="animate-spin" />}
				</Link>
			)}
		</div>
	)
}
